import React, { FC } from 'react';
import { useSubscriptionsToggleOrder } from 'hooks/useSubscriptionsToggleOrder';
import { TextV2 } from 'components/TextV2/TextV2';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_STATE, useSubscriptionsState } from 'hooks/useSubscriptionsState';
import { PRICING } from 'components/Pricing/utils/pricingHookTranslationStrings';
import { UpfrontAndMonthlyPricing } from './UpfrontAndMonthlyPricing/UpfrontAndMonthlyPricing';
import { getSubscriptionsTranslationText } from 'components/Pricing/utils/getSubscriptionsTranslationText';
import { SoftwarePlanMonthlyPricing } from './SoftwarePlanMonthlyPricing/SoftwarePlanMonthlyPricing';
import { MsrpPricing } from './MsrpPricing/MsrpPricing';
import { BooleanFilter } from 'utils/TypeUtils';

interface IPricingComponentProps {
  msrpPrice: number;
  upfrontPrice: number;
  subscriptionMonthlyPrice: number;
  softwarePlanMonthlyPrice: number;
}

/**
 * Displays pricing information based on subscription states and prices.
 *
 * Use case 1: If subscription price is not defined and subscription state is
 *    [ENABLED_36_MONTH, ENABLED_48_MONTH], returns upfront price.
 * Use case 2: If subscription price is defined and subscription state is [DISABLED], returns
 *    upfront as the primary price.
 * Use case 3: If upfront and subscription prices are defined, returns the UpfrontAndMonthlyPricing component.
 * Use case 4: If enable-msrp feature flag is enabled, shows msrp price above upfront or UpfrontAndMonthlyPricing component.
 * Use case 5: If softwarePlanMonthlyPrice is defined, shows software plan monthly price next to upfront price.
 *
 * @param {number} msrpPrice - The msrp price.
 * @param {number} upfrontPrice - The upfront price.
 * @param {number} subscriptionMonthlyPrice - The monthly subscription price.
 * @param {number | undefined } softwarePlanMonthlyPrice - The monthly price for a software plan (optional).
 *
 * @returns {JSX.Element | null} The rendered JSX for the Pricing component.
 *
 */
export const Pricing: FC<IPricingComponentProps> = ({
  msrpPrice,
  upfrontPrice,
  subscriptionMonthlyPrice,
  softwarePlanMonthlyPrice,
}) => {
  const { t } = useTranslation();
  const { isMonthlyFirst: showMonthlyPriceFirst } = useSubscriptionsToggleOrder();
  const { subscriptionsState } = useSubscriptionsState();

  const is36MonthsEnabled = subscriptionsState === SUBSCRIPTION_STATE.ENABLED_36_MONTH;
  const subscriptionsTranslationText = getSubscriptionsTranslationText(
    showMonthlyPriceFirst,
    is36MonthsEnabled,
  );
  const hasSubscriptionMonthlyPrice = BooleanFilter(subscriptionMonthlyPrice);

  if (!hasSubscriptionMonthlyPrice) {
    return (
      <>
        <MsrpPricing msrpPrice={msrpPrice} />
        <TextV2 variant="body-medium" value={t(PRICING.STRING_UPFRONT, { price: upfrontPrice })} />
        <SoftwarePlanMonthlyPricing softwarePlanMonthlyPrice={softwarePlanMonthlyPrice} />
      </>
    );
  }

  return (
    <UpfrontAndMonthlyPricing
      msrpPrice={msrpPrice}
      upfrontPrice={upfrontPrice}
      subscriptionMonthlyPrice={subscriptionMonthlyPrice}
      subscriptionsTranslationText={subscriptionsTranslationText}
      showMonthlyPriceFirst={showMonthlyPriceFirst}
      softwarePlanMonthlyPrice={softwarePlanMonthlyPrice}
    />
  );
};
